import { useState, type ReactNode } from 'react';
import {
    Box,
    Button,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const PAGE_SIZES = [25, 50, 100];

// The trader view counts in the background (spec 6.7). Without `count` the
// bar shows the event range and total as before.
export type PagerCount =
    | { kind: 'counting' }
    | { kind: 'known'; total: number }
    | { kind: 'failed' };

const SELECT_SX = {
    fontSize: '11px',
    fontFamily: 'inherit',
    color: 'text.secondary',
    bgcolor: 'transparent',
    border: 'none',
    outline: 'none',
    p: 0,
    m: 0,
    height: 16,
    lineHeight: '16px',
    cursor: 'pointer',
} as const;

export interface PaginationBarProps {
    pageIndex: number;
    pageSize: number;
    pageCount?: number;
    total?: number;
    hasPrev: boolean;
    hasNext: boolean;
    busy: boolean;
    selectedCount?: number;
    selectionActions?: ReactNode;
    pageSizes?: readonly number[];
    count?: PagerCount;
    countNoun?: { singular: string; plural: string };
    actions?: ReactNode;
    onJump?: (pageIndex: number) => void;
    onPrev: () => void;
    onNext: () => void;
    onPageSizeChange: (size: number) => void;
    onClearSelection?: () => void;
}

function JumpField({
    pages,
    disabled,
    onJump,
}: {
    pages: number;
    disabled: boolean;
    onJump: (pageIndex: number) => void;
}) {
    const [draft, setDraft] = useState('');
    return (
        <TextField
            size="small"
            type="number"
            value={draft}
            disabled={disabled}
            placeholder="Go to"
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
                if (e.key !== 'Enter') return;
                const page = Number(draft);
                if (!Number.isFinite(page) || draft.trim() === '') return;
                onJump(Math.min(pages, Math.max(1, Math.round(page))) - 1);
                setDraft('');
            }}
            inputProps={{
                'aria-label': 'Go to page',
                min: 1,
                max: pages,
                style: { fontSize: '11px', padding: '1px 4px', width: 44 },
            }}
        />
    );
}

function CountText({
    pageIndex,
    pageSize,
    count,
    noun,
}: {
    pageIndex: number;
    pageSize: number;
    count: PagerCount;
    noun: { singular: string; plural: string };
}) {
    const page = (pageIndex + 1).toLocaleString('en-US');
    const sx = { fontSize: '11px', color: 'text.secondary' };
    if (count.kind === 'counting') {
        return <Typography sx={sx}>{`Page ${page} · counting…`}</Typography>;
    }
    if (count.kind === 'failed') {
        return (
            <Tooltip title="Total unavailable" disableInteractive>
                <Typography sx={sx}>{`Page ${page}`}</Typography>
            </Tooltip>
        );
    }
    const pages = Math.max(1, Math.ceil(count.total / pageSize));
    return (
        <Typography sx={sx}>
            {`Page ${page} of ${pages.toLocaleString('en-US')} · ${count.total.toLocaleString('en-US')} ${count.total === 1 ? noun.singular : noun.plural}`}
        </Typography>
    );
}

export function PaginationBar({
    pageIndex,
    pageSize,
    pageCount = 0,
    total = 0,
    hasPrev,
    hasNext,
    busy,
    selectedCount = 0,
    selectionActions,
    pageSizes = PAGE_SIZES,
    count,
    countNoun = { singular: 'result', plural: 'results' },
    actions,
    onJump,
    onPrev,
    onNext,
    onPageSizeChange,
    onClearSelection,
}: PaginationBarProps) {
    const start = total === 0 ? 0 : pageIndex * pageSize + 1;
    const end = pageIndex * pageSize + pageCount;
    const pages = pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
    const knownPages =
        count?.kind === 'known'
            ? Math.max(1, Math.ceil(count.total / pageSize))
            : null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                borderTop: 1,
                borderColor: 'divider',
                px: 1.25,
                height: 28,
                flexWrap: 'nowrap',
                whiteSpace: 'nowrap',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'nowrap',
                    whiteSpace: 'nowrap',
                }}
            >
                {count ? (
                    <>
                        <CountText
                            pageIndex={pageIndex}
                            pageSize={pageSize}
                            count={count}
                            noun={countNoun}
                        />
                        {knownPages !== null && knownPages > 1 && onJump && (
                            <JumpField
                                pages={knownPages}
                                disabled={busy}
                                onJump={onJump}
                            />
                        )}
                    </>
                ) : (
                    <Typography
                        sx={{ fontSize: '11px', color: 'text.secondary' }}
                    >
                        {total === 0
                            ? 'No events'
                            : `${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}`}
                    </Typography>
                )}

                {actions}

                {selectedCount > 0 && (
                    <>
                        <Typography
                            sx={{
                                fontSize: '11px',
                                color: 'primary.main',
                                fontWeight: 700,
                            }}
                        >
                            {selectedCount.toLocaleString()} selected
                        </Typography>
                        <Button
                            size="small"
                            onClick={onClearSelection}
                            sx={{ fontSize: '10px', minWidth: 0, p: 0.25 }}
                        >
                            Clear
                        </Button>
                        {selectionActions}
                    </>
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'nowrap',
                    whiteSpace: 'nowrap',
                }}
            >
                <Box
                    component="select"
                    value={pageSize}
                    onChange={e =>
                        onPageSizeChange(
                            Number((e.target as HTMLSelectElement).value)
                        )
                    }
                    sx={SELECT_SX}
                >
                    {pageSizes.map(size => (
                        <option key={size} value={size}>
                            {size} / page
                        </option>
                    ))}
                </Box>

                {!count && (
                    <Typography
                        sx={{ fontSize: '11px', color: 'text.disabled' }}
                    >
                        Page {pageIndex + 1} of {pages.toLocaleString()}
                    </Typography>
                )}

                <IconButton
                    size="small"
                    aria-label="Previous page"
                    onClick={onPrev}
                    disabled={!hasPrev || busy}
                    sx={{ p: 0.15 }}
                >
                    <ChevronLeftIcon sx={{ fontSize: 17 }} />
                </IconButton>
                <IconButton
                    size="small"
                    aria-label="Next page"
                    onClick={onNext}
                    disabled={!hasNext || busy}
                    sx={{ p: 0.15 }}
                >
                    <ChevronRightIcon sx={{ fontSize: 17 }} />
                </IconButton>
            </Box>
        </Box>
    );
}
